import { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { CalendarClock, Undo2 } from 'lucide-react-native';
import { addDays, colors, crewLabels, formatDayInSentence, isoOf, parseDate, weekdayOf, weekdayShort, type ApiDayMove, type Crew } from '@hogar/shared';
import { Button, Card, Label, RowLink, Screen, Segmented, Txt, confirm, notice, showProblem, useT } from '../components/ui';
import { BackHeader } from '../components/BackHeader';
import { api, ApiError } from '../lib/api';
import { useHouse } from '../lib/house';
import { hasGarden } from '../lib/crew';
import { useAction } from '../lib/session';

/**
 * Moving a day, one time (Mike's wife, 2026-10-22): "she can't come Monday, she comes Tuesday."
 * The day's unfinished tasks go to the new date, on top of that date's own routine; the weekly
 * routine does not change, and the move can be undone.
 */
export default function MoveDay() {
  const { t, lang } = useT();
  const params = useLocalSearchParams<{ crew?: string; from?: string }>();
  const snap = useHouse();
  const { today, plans, moves } = snap;
  const act = useAction();
  const garden = hasGarden(snap);
  const [crew, setCrew] = useState<Crew>(params.crew === 'garden' ? 'garden' : 'house');
  const mine = moves.filter((m) => m.crew === crew);
  const busy = new Set(mine.flatMap((m) => [m.fromDate, m.toDate]));
  const days = (first: number, last: number) => Array.from({ length: last - first + 1 }, (_, i) => isoOf(addDays(parseDate(today.date), first + i)));
  const count = (iso: string) => plans[iso]?.[crew]?.length ?? 0;
  // A day can be moved if it has tasks and is not already part of a change; yesterday too (a day she missed).
  const fromDays = days(-1, 13).filter((d) => count(d) > 0 && !busy.has(d));
  const [from, setFrom] = useState<string | undefined>(params.from && fromDays.includes(params.from) ? params.from : fromDays.find((d) => d >= today.date));
  const toDays = days(0, 13).filter((d) => d !== from && !busy.has(d));
  const [to, setTo] = useState<string | undefined>();
  const target = to && toDays.includes(to) ? to : undefined;
  const fmt = (iso: string) => formatDayInSentence(parseDate(iso), lang);
  const pending = from ? (plans[from]?.[crew] ?? []).filter((id) => from !== today.date || !today.results[id]).length : 0;

  const move = async () => {
    if (!from || !target) return;
    try {
      await act(() => api('POST', '/day-moves', { crew, fromDate: from, toDate: target }));
      router.canGoBack() ? router.back() : router.replace('/');
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      if (code === 'nothing_to_move') notice(t('moveNothing'));
      else if (code === 'already_moved') notice(t('moveAlready'));
      else showProblem(t('errGeneric'), lang);
    }
  };

  const undo = async (m: ApiDayMove) => {
    if (!(await confirm(t('moveUndoConfirm'), t('moveUndo'), t('cancel')))) return;
    await act(() => api('DELETE', `/day-moves/${m.id}`)).catch(() => showProblem(t('errGeneric'), lang));
  };

  return (
    <Screen>
      <BackHeader title={t('moveDay')} subtitle={t('moveDaySub')} fallback="/" />
      {garden && (
        <View style={{ marginBottom: 14 }}>
          <Segmented<Crew> value={crew} options={[{ value: 'house', label: crewLabels.house[lang] }, { value: 'garden', label: crewLabels.garden[lang] }]} onChange={(c) => { setCrew(c); setFrom(undefined); setTo(undefined); }} />
        </View>
      )}

      {fromDays.length === 0 ? (
        <Txt color={colors.textMuted} style={{ marginVertical: 12 }}>{t('moveNoDays')}</Txt>
      ) : (
        <>
          <Label>{t('moveFrom')}</Label>
          <DayChips days={fromDays} value={from} onPick={(d) => { setFrom(d); if (d === to) setTo(undefined); }} count={count} today={today.date} />
          <Label>{t('moveTo')}</Label>
          <DayChips days={toDays} value={target} onPick={setTo} count={count} today={today.date} />
          {from && target && (
            <Card style={{ paddingVertical: 14, marginTop: 14 }}>
              <Txt size={15}>{t(pending === 1 ? 'moveSummaryOne' : 'moveSummary', { n: pending, from: fmt(from), to: fmt(target) })}</Txt>
            </Card>
          )}
          <Button label={t('moveConfirm')} Icon={CalendarClock} onPress={move} disabled={!from || !target || pending === 0} style={{ marginTop: 14 }} />
        </>
      )}

      {mine.length > 0 && (
        <>
          <Txt weight="semibold" size={16} style={{ marginTop: 24, marginBottom: 8 }}>{t('movesTitle')}</Txt>
          <Card>
            {mine.map((m, i) => (
              <RowLink key={m.id} Icon={Undo2} title={t('moveLine', { from: fmt(m.fromDate), to: fmt(m.toDate), n: m.count })} subtitle={t('moveUndo')} onPress={() => undo(m)} last={i === mine.length - 1} />
            ))}
          </Card>
        </>
      )}
    </Screen>
  );
}

/** A row of dates to pick from: weekday, day of the month, and how many tasks are on it. */
function DayChips({ days, value, onPick, count, today }: { days: string[]; value?: string; onPick: (d: string) => void; count: (d: string) => number; today: string }) {
  const { t, lang } = useT();
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
      {days.map((d) => {
        const date = parseDate(d);
        const on = d === value;
        const n = count(d);
        return (
          <Pressable
            key={d}
            onPress={() => onPick(d)}
            accessibilityRole="button"
            accessibilityState={{ selected: on }}
            style={{ width: 64, paddingVertical: 10, alignItems: 'center', borderRadius: 14, borderWidth: 1.5, borderColor: on ? colors.primary : colors.cardBorder, backgroundColor: on ? colors.primarySoft : colors.card }}
          >
            <Txt size={13} color={colors.textMuted}>{d === today ? t('todayShort') : weekdayShort(weekdayOf(date), lang)}</Txt>
            <Txt weight="semibold" size={18}>{date.getDate()}</Txt>
            <Txt size={12} color={colors.textMuted}>{n === 0 ? '—' : n}</Txt>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
